import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import { Translate } from "@/app/application/Translate";
import SidebarLogoLight from "@/assets/svg/logo/logo-satos-red.svg?react";
import SidebarLogoDark from "@/assets/svg/logo/logo-satos-white-dashboard.svg?react";

export type AuthStatusState = "checking" | "redirecting" | "denied" | "error";
export type AuthStatusReason = "expired";

const getAuthStatusMessage = (
	status: AuthStatusState,
	reason?: AuthStatusReason,
): React.ReactNode => {
	if (status === "denied") {
		return (
			<Translate String="Auth.Status.AccessDenied">
				You don't have access to this application. Contact your administrator.
			</Translate>
		);
	} else if (status === "error") {
		return (
			<Translate String="Auth.Status.Error">
				Something went wrong. Please try again later.
			</Translate>
		);
	} else if (reason === "expired") {
		return (
			<Translate String="Auth.Status.SessionExpired">
				Session expired. Signing you in...
			</Translate>
		);
	} else if (status === "checking") {
		return (
			<Translate String="Auth.Status.CheckingSession">
				Checking session...
			</Translate>
		);
	}

	return (
		<Translate String="Auth.Status.SigningIn">Signing you in...</Translate>
	);
};

export const AuthStatus = ({
	status,
	reason,
}: {
	status: AuthStatusState;
	reason?: AuthStatusReason;
}) => {
	const theme = useTheme();
	const message = getAuthStatusMessage(status, reason);

	return (
		<Box
			sx={(theme) => ({
				position: "fixed",
				inset: 0,
				display: "grid",
				placeItems: "center",
				background:
					theme.palette.mode === "dark"
						? `radial-gradient(1200px 700px at 20% 15%, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 55%), linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
						: `radial-gradient(1200px 700px at 20% 15%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 55%), linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
				px: 2,
				py: 4,
			})}
		>
			<Box
				sx={(theme) => ({
					width: "min(92vw, 420px)",
					borderRadius: 3,
					px: { xs: 3, sm: 4 },
					py: { xs: 3, sm: 4 },
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 1.5,
					textAlign: "center",
					backgroundColor: alpha(theme.palette.background.paper, 0.86),
					backdropFilter: "blur(10px)",
					border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.55 : 0.85)}`,
					boxShadow:
						theme.palette.mode === "dark"
							? "0 18px 48px rgba(0, 0, 0, 0.38)"
							: "0 14px 36px rgba(15, 23, 42, 0.12)",
				})}
			>
				<Box
					sx={(theme) => ({
						width: 56,
						height: 56,
						borderRadius: 2,
						display: "grid",
						placeItems: "center",
						backgroundColor: alpha(
							theme.palette.primary.main,
							theme.palette.mode === "dark" ? 0.2 : 0.1,
						),
						border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.45 : 0.25)}`,
					})}
				>
					{theme.palette.mode === "dark" ? (
						<SidebarLogoDark
							aria-hidden
							style={{ width: 30, height: 30, display: "block" }}
						/>
					) : (
						<SidebarLogoLight
							aria-hidden
							style={{ width: 30, height: 30, display: "block" }}
						/>
					)}
				</Box>
				<Typography
					variant="subtitle2"
					sx={{ color: "text.primary", letterSpacing: "0.04em" }}
				>
					<Translate String="Auto.Auth.Ui.AuthStatus.L128C6.SATOS">SATOS</Translate>
				</Typography>
				{status !== "denied" && status !== "error" && (
					<CircularProgress
						size={28}
						thickness={4.2}
						sx={{ color: "primary.main" }}
					/>
				)}
				<Typography variant="body2" sx={{ color: "text.secondary" }}>
					{message}
				</Typography>
				{status === "denied" && (
					<Button
						component="a"
						href={`/api/auth/logout?postLogoutRedirectUri=${encodeURIComponent(window.location.origin)}`}
						variant="outlined"
						startIcon={<LogoutRoundedIcon />}
						sx={{ mt: 1 }}
					>
						<Translate String="Frontpage.Header.Logout">Logout</Translate>
					</Button>
				)}
				{status === "error" && (
					<Button
						onClick={() => window.location.reload()}
						variant="outlined"
						startIcon={<RefreshRoundedIcon />}
						sx={{ mt: 1 }}
					>
						<Translate String="Auth.Status.Refresh">Refresh</Translate>
					</Button>
				)}
			</Box>
		</Box>
	);
};
